import React from 'react';
import { graphql, Link as GatsbyLink, useStaticQuery } from 'gatsby';
import { useIntl } from 'react-intl';

import { IconButton } from '@entur/button';
import { FacebookIcon, InstagramIcon, LinkedinIcon } from '@entur/icons';
import { Contrast, type ContrastProps } from '@entur/layout';
import { Heading3, Link as LinkText, Paragraph } from '@entur/typography';

import * as ROUTES from '~/lib/routes';

import './Footer.scss';
import { Flex, Grid } from '@entur/layout/beta';
import { fullWidthColSpan } from '~/utils/contentWrapperColSpan';

const ContrastFooter = (props: ContrastProps<'footer'>) => (
  <Contrast as="footer" {...props} />
);

export const Footer = () => {
  const { formatMessage: f } = useIntl();
  const data = useStaticQuery(graphql`
    query {
      logo: file(relativePath: { eq: "LogoNegative.svg" }) {
        publicURL
      }
    }
  `);

  return (
    <Grid.Item
      as={ContrastFooter}
      className="omtur-footer"
      colSpan={fullWidthColSpan}
    >
      <div className="omtur-footer__content-wrapper">
        <div className="omtur-footer__header">
          <img
            alt=""
            aria-hidden="true"
            src={data.logo.publicURL}
            width="104"
          />
        </div>

        <Grid gap="m">
          <Grid.Item
            colSpan={{ base: 12, m: 3 }}
            className="omtur-footer__vertical-section"
          >
            <Heading3 as="h2" margin="bottom">
              Entur AS
            </Heading3>
            <Paragraph margin="none">
              Rådhusgata 5, 0151 Oslo
              <br />
              Postboks 1554 Vika, 0117 Oslo
            </Paragraph>
            <Paragraph margin="none">
              {f({ id: 'footer.orgnr' })}: <br /> 917 422 575
            </Paragraph>
            <LinkText
              as={GatsbyLink}
              to={ROUTES.kontaktOss.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.kontaktOss.nameAlt })}
            </LinkText>
            <LinkText
              className="omtur-footer__link"
              href="mailto:webredaktor@entur.org"
              target="_blank"
              external
            >
              {f({ id: 'footer.webredaktor' })}
            </LinkText>
          </Grid.Item>
          <Grid.Item
            colSpan={{ base: 12, m: 3 }}
            className="omtur-footer__vertical-section"
          >
            <Heading3 as="h2" margin="bottom">
              {f({ id: 'footer.informasjon' })}
            </Heading3>

            <LinkText
              as={GatsbyLink}
              to={ROUTES.nettstedkart.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.nettstedkart.name })}
            </LinkText>

            <LinkText
              as={GatsbyLink}
              to={ROUTES.personvern.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.personvern.name })}
            </LinkText>

            <LinkText
              onClick={() => {
                if (typeof UC_UI !== 'undefined') UC_UI?.showFirstLayer();
              }}
              className="omtur-footer__link"
            >
              {f({ id: 'footer.cookieSettings' })}
            </LinkText>

            <LinkText
              className="omtur-footer__link"
              href="https://uustatus.no/nb/erklaringer/publisert/5aa4d523-635f-41a3-9a9e-f794e8404777"
              target="_blank"
              external
            >
              {f({ id: 'footer.tilgjengelighetserklaering' })}
            </LinkText>

            <LinkText
              as={GatsbyLink}
              to={ROUTES.vilkar.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.vilkar.name })}
            </LinkText>

            <LinkText
              as={GatsbyLink}
              to={ROUTES.vedtekter.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.vedtekter.name })}
            </LinkText>
          </Grid.Item>
          <Grid.Item
            colSpan={{ base: 12, m: 3 }}
            className="omtur-footer__vertical-section"
          >
            <Heading3 as="h2" margin="bottom">
              {f({ id: 'footer.tjenester' })}
            </Heading3>

            <LinkText
              href={ROUTES.tjenester.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.tjenester.name })}
            </LinkText>

            <LinkText
              as={GatsbyLink}
              to={ROUTES.lastnedapp.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.lastnedapp.name })}
            </LinkText>

            <LinkText
              href={ROUTES.datakatalog.url}
              className="omtur-footer__link"
              external
            >
              {f({ id: ROUTES.datakatalog.name })}
            </LinkText>

            <LinkText
              href={ROUTES.designPortal.url}
              className="omtur-footer__link"
              external
            >
              {f({ id: ROUTES.designPortal.name })}
            </LinkText>

            <LinkText
              href={ROUTES.utviklerportal.url}
              className="omtur-footer__link"
              external
            >
              {f({ id: ROUTES.utviklerportal.name })}
            </LinkText>

            <LinkText
              className="omtur-footer__link"
              href={ROUTES.incidentPortal.url}
              external
            >
              {f({ id: ROUTES.incidentPortal.name })}
            </LinkText>
          </Grid.Item>
          <Grid.Item as={Flex} gap="xs" align="start" colSpan={{ base: 12, m: 3 }}>
            <IconButton
              as="a"
              href="https://www.facebook.com/entur.org/"
              aria-label={`${f({ id: 'footer.linkTo' })} Facebook`}
            >
              <FacebookIcon />
            </IconButton>
            <IconButton
              as="a"
              href="https://www.instagram.com/entur_as/"
              aria-label={`${f({ id: 'footer.linkTo' })} Instagram`}
            >
              <InstagramIcon />
            </IconButton>
            <IconButton
              as="a"
              href="https://www.linkedin.com/company/entur-as/"
              aria-label={`${f({ id: 'footer.linkTo' })} LinkedIn`}
            >
              <LinkedinIcon />
            </IconButton>
          </Grid.Item>
        </Grid>
      </div>
    </Grid.Item>
  );
};
